/**
 * PORTED (REWRITTEN) — RO-3-00d / #44471.
 * Origin: `excellent/app/projects/[projectSlug]/[sectionSlug]/_crm/opportunities.tsx`.
 *
 * List + pipeline views compose already-mounted `crm.opportunities.list` and
 * `crm.pipelines.list`. New/detail forms reuse co-ported client glue.
 */

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataTable,
  EmptyState,
  PageHeader,
  formatDate,
  ObjectLink,
  type DataTableColumn,
} from "@excellent/ui";
import { Plus } from "@excellent/ui/icons";
import { RouterLink } from "@excellent/ui/router";
import { formatMoneyMinor, formatRelativeTime } from "@excellent/ui/blocks/crm/format";
import { useApi } from "@excellent/ui/api";
import { crm } from "@excellent/ui/api/contracts/crm";
import {
  NewOpportunityClient,
  OpportunityMoveClient,
  PipelineBoardClient,
} from "./client";

type OppRow = {
  id: number;
  uid?: string;
  name: string;
  stage_id: number;
  value_minor: number;
  currency: string;
  probability_pct: number | null;
  updated_at: string;
  lead_id: number;
};

export function OpportunitiesView({ basePath }: { basePath: string }) {
  const { data: pipelinesData } = useApi(crm.pipelines.list, {});
  const { data: oppsData, error, isLoading } = useApi(crm.opportunities.list, {
    query: {},
  });
  const pipelines = pipelinesData?.pipelines ?? [];
  const stageById = new Map<number, { name: string; pipeline_id: number }>();
  for (const p of pipelines) {
    for (const s of p.stages ?? []) {
      stageById.set(s.id, { name: s.name, pipeline_id: p.id });
    }
  }
  const opps: OppRow[] = (oppsData?.opportunities ?? []).map((o) => ({
    id: o.id,
    uid: o.uid,
    name: o.name,
    stage_id: o.stage_id,
    value_minor: o.value_minor,
    currency: o.currency,
    probability_pct: o.probability_pct,
    updated_at: o.updated_at,
    lead_id: o.lead_id,
  }));
  const scoped = !basePath.startsWith("/projects/sections");

  const columns: DataTableColumn<OppRow>[] = [
    {
      key: "name",
      header: "Name",
      render: (o) =>
        o.uid ? (
          <ObjectLink className="text-foreground hover:underline" uid={o.uid} from="customers">
            {o.name}
          </ObjectLink>
        ) : (
          <span className="text-foreground">{o.name}</span>
        ),
    },
    {
      key: "stage",
      header: "Stage",
      width: "w-40",
      cellClassName: "text-muted-foreground",
      render: (o) => stageById.get(o.stage_id)?.name ?? "—",
    },
    {
      key: "value",
      header: "Value",
      width: "w-32",
      cellClassName: "text-muted-foreground tabular-nums",
      render: (o) => formatMoneyMinor(o.value_minor, o.currency),
    },
    {
      key: "prob",
      header: "Prob.",
      width: "w-24",
      cellClassName: "text-muted-foreground tabular-nums",
      render: (o) => o.probability_pct ?? "—",
    },
    {
      key: "updated",
      header: "Updated",
      width: "w-32",
      cellClassName: "text-muted-foreground",
      render: (o) => formatRelativeTime(o.updated_at),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Opportunities"
        description="Track every deal — its value, stage, and where it stands."
        toolbar={
          scoped ? (
            <Button asChild size="icon-tb">
              <RouterLink
                href={`${basePath}/opportunities/new`}
                aria-label="New opportunity"
                title="New opportunity"
              >
                <Plus className="size-4" />
              </RouterLink>
            </Button>
          ) : undefined
        }
      />
      {error ? (
        <p className="type-body text-destructive">{error.message ?? String(error)}</p>
      ) : null}
      {isLoading && !oppsData ? (
        <p className="type-body text-muted-foreground">Loading…</p>
      ) : opps.length === 0 ? (
        <EmptyState
          title="No opportunities yet"
          description="Create a deal and attach it to a lead, or open a lead to add one in context."
          action={
            scoped ? (
              <Button asChild size="icon-tb">
                <RouterLink
                  href={`${basePath}/opportunities/new`}
                  aria-label="New opportunity"
                  title="New opportunity"
                >
                  <Plus className="size-4" />
                </RouterLink>
              </Button>
            ) : undefined
          }
        />
      ) : (
        <DataTable rows={opps} columns={columns} rowKey={(o) => o.id} />
      )}
    </div>
  );
}

export function NewOpportunityView({
  basePath,
  presetLeadId,
}: {
  basePath: string;
  presetLeadId?: number;
}) {
  // page_size is the contract field (max 200); `limit` is silently dropped.
  const { data: leadsData } = useApi(crm.leads.list, {
    query: { page_size: 200 },
  });
  const { data: pipelinesData } = useApi(crm.pipelines.list, {});
  const leadOptions = (leadsData?.rows ?? []).map((l) => ({
    id: l.id,
    display_name: l.display_name,
  }));
  const pipelines = pipelinesData?.pipelines ?? [];

  return (
    <div className="mx-auto flex max-w-xl flex-col gap-4">
      <PageHeader
        title="New opportunity"
        description="Start a new deal and link it to the lead it belongs to."
      />
      <NewOpportunityClient
        basePath={basePath}
        presetLeadId={presetLeadId ?? null}
        leads={leadOptions.map((l) => ({ id: l.id, display_name: l.display_name }))}
        pipelines={pipelines as Parameters<typeof NewOpportunityClient>[0]["pipelines"]}
        owners={[]}
      />
    </div>
  );
}

export function OpportunityDetailView({
  basePath,
  id,
}: {
  basePath: string;
  id: string;
}) {
  const oppId = Number(id);
  const { data: opp, error, isLoading } = useApi(crm.opportunities.get, {
    params: { id: oppId },
  });
  const { data: pipelinesData } = useApi(crm.pipelines.list, {});
  const pipelines = pipelinesData?.pipelines ?? [];
  const stages = pipelines.flatMap((p) => p.stages ?? []);

  if (isLoading && !opp) {
    return <p className="type-body text-muted-foreground">Loading…</p>;
  }
  if (error || !opp) {
    return (
      <EmptyState
        title="Opportunity not found"
        description={error?.message ?? "This deal may have been archived."}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={opp.name} description={`Updated ${formatDate(opp.updated_at)}`} />
      <OpportunityMoveClient
        opportunity={{
          id: opp.id,
          stage_id: opp.stage_id,
          value_minor: opp.value_minor ?? 0,
          currency: opp.currency,
        }}
        stages={stages.map((s) => ({ id: s.id, name: s.name }))}
      />
      <Card>
        <CardHeader>
          <CardTitle>Value</CardTitle>
        </CardHeader>
        <CardContent className="type-body text-muted-foreground">
          {formatMoneyMinor(opp.value_minor, opp.currency)} ·{" "}
          {opp.probability_pct ?? "—"}% · base {basePath}
        </CardContent>
      </Card>
    </div>
  );
}

export function PipelineView({
  basePath,
  pipelineId,
}: {
  basePath: string;
  pipelineId?: number;
}) {
  const { data: pipelinesData } = useApi(crm.pipelines.list, {});
  const { data: oppsData } = useApi(crm.opportunities.list, { query: {} });
  const pipelines = pipelinesData?.pipelines ?? [];
  const primary =
    (pipelineId ? pipelines.find((p) => p.id === pipelineId) : null) ??
    pipelines.find((p) => p.is_default === 1) ??
    pipelines[0];
  const opps = oppsData?.opportunities ?? [];

  if (!primary) {
    return (
      <EmptyState
        title="No pipeline yet"
        description="Create a pipeline under Customers settings to board deals by stage."
      />
    );
  }

  return (
    <PipelineBoardClient
      basePath={basePath}
      stages={(primary.stages ?? []).map((s) => ({
        id: s.id,
        slug: s.slug,
        name: s.name,
        probability_pct: s.probability_pct,
        is_won: s.is_won,
        is_lost: s.is_lost,
      }))}
      opportunities={opps.map((o) => ({
        id: o.id,
        uid: o.uid ?? `opportunity-${o.id}`,
        name: o.name,
        value_minor: o.value_minor,
        currency: o.currency,
        probability_pct: o.probability_pct,
        lead_id: o.lead_id,
        closed_won: o.closed_won,
        stage_id: o.stage_id,
      }))}
    />
  );
}
